/**
 * Dialog shown when a user starts a new arche. Asks them to pick a path —
 * Guided, Chat, or Plugin — and creates a session (Guided/Chat) or shows
 * local-setup instructions (Plugin). The chosen mode is locked for the
 * session's lifetime; there is no runtime toggle.
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  MessageSquare,
  Package,
  ArrowLeft,
  Copy,
  Check,
  Apple,
  Monitor,
  Globe2,
  ChevronDown,
} from 'lucide-react';

type View = 'choose' | 'plugin-setup';
type OS = 'mac' | 'pc' | 'other';

export interface NewArcheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the locked mode. The parent is responsible for creating the session + navigating. */
  onSelectMode: (mode: 'chat' | 'guided') => Promise<void> | void;
  /** Disable the Guided/Chat buttons while a session is being created. */
  isCreating?: boolean;
}

// A single sentence — the user pastes this whole thing into Claude Code (or
// equivalent) as their first prompt. Kept as one block so it's one copy.
const PLUGIN_INCANTATION =
  'Run npm create @fias/fias-plugin my-arche && cd my-arche && npm install. Read CLAUDE.md for the full SDK reference. Then wait for me to describe what I want to build.';

// Hand-hold prompts: for users who want a generic AI chat (Claude.ai, ChatGPT,
// etc.) to walk them through the whole setup one step at a time. They're
// built from the same step content as the rendered StepList above, in plain
// text so they paste cleanly into any chatbot. Keep PLUGIN_INCANTATION as
// the single source of truth for the final prompt.
const AI_WALKTHROUGH_PREAMBLE =
  "Please take me step by step through everything I need to do to implement this, and ask me for input or confirmation after each step so we can make sure it's working before moving on. If I hit errors, please help me troubleshoot.";

const AI_WALKTHROUGH_CLOSING =
  "Please start with step 1 and wait for me to confirm it's done before moving to the next step.";

const MAC_AI_PROMPT = `${AI_WALKTHROUGH_PREAMBLE}

I'm on a Mac and I want to set up the "Fias Plugin" workflow so I can build a Fias Arche (a plugin / small app) locally using an AI coding agent.

Here are the steps I need to complete:

1. Install Node.js (version 18 or newer). The easiest way is via Homebrew (https://brew.sh) by running \`brew install node\` in Terminal, or by downloading an installer from https://nodejs.org.

2. Install Claude Code globally by running \`npm install -g @anthropic-ai/claude-code\`. (Cursor, Aider, or another AI coding agent would also work.)

3. Open Terminal, run \`claude\`, and log in when prompted.

4. Once Claude Code is running, paste this into it as the very first prompt:

"${PLUGIN_INCANTATION}"

${AI_WALKTHROUGH_CLOSING}`;

const PC_AI_PROMPT = `${AI_WALKTHROUGH_PREAMBLE}

I'm on Windows and I want to set up the "Fias Plugin" workflow so I can build a Fias Arche (a plugin / small app) locally using an AI coding agent.

Here are the steps I need to complete:

1. Open PowerShell as administrator and install WSL by running \`wsl --install\`. Reboot when it finishes.

2. Launch the Ubuntu terminal that WSL installed, then install Node.js (18+). A quick route is \`curl -fsSL https://deb.nodesource.com/setup_20.x | sudo -E bash -\` followed by \`sudo apt-get install -y nodejs\`.

3. Still inside WSL, install Claude Code: \`npm install -g @anthropic-ai/claude-code\`. (Cursor, Aider, or another AI coding agent would also work.)

4. Run \`claude\` from the WSL terminal and log in when prompted.

5. Once Claude Code is running, paste this into it as the very first prompt:

"${PLUGIN_INCANTATION}"

${AI_WALKTHROUGH_CLOSING}`;

const OTHER_AI_PROMPT = `${AI_WALKTHROUGH_PREAMBLE}

I want to set up the "Fias Plugin" workflow so I can build a Fias Arche (a plugin / small app) locally using an AI coding agent. I'm on Linux or another Unix-like system.

Here are the steps I need to complete:

1. Make sure Node.js 18+ and \`npm\` are installed and on my PATH. If my distro ships an old Node, install a newer one via my package manager or nvm (https://github.com/nvm-sh/nvm).

2. Install Claude Code: \`npm install -g @anthropic-ai/claude-code\`. Any other AI coding agent that can run shell commands and edit files also works.

3. Run my AI agent and log in. I'll need access to the public npm registry so the \`@fias/fias-plugin\` scaffold can be pulled.

4. Once the agent is running, paste this into it as the very first prompt:

"${PLUGIN_INCANTATION}"

${AI_WALKTHROUGH_CLOSING}`;

export function NewArcheDialog({
  open,
  onOpenChange,
  onSelectMode,
  isCreating = false,
}: NewArcheDialogProps) {
  const [view, setView] = useState<View>('choose');
  const [os, setOs] = useState<OS>('mac');

  // Reset internal view when the dialog closes so reopening lands on the picker.
  const handleOpenChange = (next: boolean) => {
    if (!next) setView('choose');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {view === 'choose' ? (
          <ChooseView
            onGuided={() => onSelectMode('guided')}
            onChat={() => onSelectMode('chat')}
            onPlugin={() => setView('plugin-setup')}
            isCreating={isCreating}
          />
        ) : (
          <PluginSetupView os={os} onOsChange={setOs} onBack={() => setView('choose')} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Choose view ────────────────────────────────────────────────────────────

function ChooseView({
  onGuided,
  onChat,
  onPlugin,
  isCreating,
}: {
  onGuided: () => void;
  onChat: () => void;
  onPlugin: () => void;
  isCreating: boolean;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Start a new Arche</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Pick how you want to build. You can&apos;t switch later, but you can start another Arche
          any time.
        </p>
      </DialogHeader>

      <div className="grid gap-3 md:grid-cols-3 mt-2">
        <ChoiceCard
          icon={<Sparkles className="h-6 w-6" />}
          title="Guided"
          description="Answer a few questions and Fias AI plans, then builds your Arche step by step. Best when you're not sure exactly what you want yet, or the idea is complex."
          onClick={onGuided}
          disabled={isCreating}
        />
        <ChoiceCard
          icon={<MessageSquare className="h-6 w-6" />}
          title="Chat"
          description="Describe what you want in free-form conversation and Fias AI builds it iteratively. Best for quick prototypes, simple Arches, and direct control over each change."
          onClick={onChat}
          disabled={isCreating}
        />
        <ChoiceCard
          icon={<Package className="h-6 w-6" />}
          title="Plugin"
          description="Build locally with Claude Code (or another AI coding agent) and our npm package. Best for more complex projects, while still easy to use for everyone."
          onClick={onPlugin}
          disabled={isCreating}
        />
      </div>
    </>
  );
}

function ChoiceCard({
  icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex flex-col items-start gap-3 rounded-lg border bg-card p-4 text-left transition-all',
        'hover:border-primary hover:shadow-md hover:-translate-y-0.5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
      )}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        {icon}
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </button>
  );
}

// ─── Plugin setup view ──────────────────────────────────────────────────────

function PluginSetupView({
  os,
  onOsChange,
  onBack,
}: {
  os: OS;
  onOsChange: (os: OS) => void;
  onBack: () => void;
}) {
  const [showAiWalkthrough, setShowAiWalkthrough] = useState(false);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <DialogTitle>Set up the Plugin workflow</DialogTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Build locally with Claude Code (or any AI coding agent that can run shell commands and
          edit files).
        </p>
      </DialogHeader>

      <div className="flex gap-1 rounded-md bg-muted p-1 w-fit">
        <OsTab
          active={os === 'mac'}
          onClick={() => onOsChange('mac')}
          icon={<Apple className="h-3.5 w-3.5" />}
        >
          Mac
        </OsTab>
        <OsTab
          active={os === 'pc'}
          onClick={() => onOsChange('pc')}
          icon={<Monitor className="h-3.5 w-3.5" />}
        >
          Windows
        </OsTab>
        <OsTab
          active={os === 'other'}
          onClick={() => onOsChange('other')}
          icon={<Globe2 className="h-3.5 w-3.5" />}
        >
          Other
        </OsTab>
      </div>

      <div className="space-y-4">
        {os === 'mac' && <MacInstructions />}
        {os === 'pc' && <PcInstructions />}
        {os === 'other' && <OtherInstructions />}

        <div>
          <p className="text-sm font-medium mb-2">
            Then paste this into Claude Code (or your AI agent) as your first prompt:
          </p>
          <CopyBlock text={PLUGIN_INCANTATION} />
          <p className="text-xs text-muted-foreground mt-2">
            It scaffolds the project, installs dependencies, and primes the AI with our SDK
            reference so your next message can describe what you want to build.
          </p>
        </div>

        <Collapsible open={showAiWalkthrough} onOpenChange={setShowAiWalkthrough}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-muted/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span>
                Want an AI to walk you through every step?{' '}
                <span className="text-muted-foreground font-normal">
                  {showAiWalkthrough ? 'Click to hide.' : 'Click to expand.'}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 transition-transform',
                  showAiWalkthrough && 'rotate-180',
                )}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <p className="text-sm mb-2">
              Paste this into{' '}
              <a
                href="https://claude.ai"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-primary"
              >
                Claude.ai
              </a>
              ,{' '}
              <a
                href="https://chatgpt.com"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-primary"
              >
                ChatGPT
              </a>
              , or any other AI chat:
            </p>
            <CopyBlock
              text={os === 'mac' ? MAC_AI_PROMPT : os === 'pc' ? PC_AI_PROMPT : OTHER_AI_PROMPT}
            />
            <p className="text-xs text-muted-foreground mt-2">
              The AI will guide you one step at a time, wait for your confirmation, and help you
              troubleshoot if you get stuck.
            </p>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </>
  );
}

function OsTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function MacInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Install Node.js (18 or newer) — the easiest path is <Code>brew install node</Code> via{' '}
        <a
          href="https://brew.sh"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-primary"
        >
          Homebrew
        </a>
        , or grab an installer from{' '}
        <a
          href="https://nodejs.org"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-primary"
        >
          nodejs.org
        </a>
        .
      </Step>
      <Step num={2}>
        Install Claude Code globally: <Code>npm install -g @anthropic-ai/claude-code</Code> (or use
        Cursor, Aider, or any other AI coding agent you prefer).
      </Step>
      <Step num={3}>
        Open Terminal, run <Code>claude</Code>, and log in when prompted.
      </Step>
    </StepList>
  );
}

function PcInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Open PowerShell as administrator and install WSL: <Code>wsl --install</Code>. Reboot when it
        finishes.
      </Step>
      <Step num={2}>
        Launch the Ubuntu terminal WSL installed for you, then install Node.js (18+). A quick route
        is <Code>curl -fsSL https://deb.nodesource.com/setup_20.x | sudo -E bash -</Code> followed
        by <Code>sudo apt-get install -y nodejs</Code>.
      </Step>
      <Step num={3}>
        Still inside WSL, install Claude Code: <Code>npm install -g @anthropic-ai/claude-code</Code>{' '}
        (or Cursor / Aider / another AI coding agent).
      </Step>
      <Step num={4}>
        Run <Code>claude</Code> from the WSL terminal and log in when prompted.
      </Step>
    </StepList>
  );
}

function OtherInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Make sure you have Node.js 18+ and <Code>npm</Code> on your PATH. If your distro ships a
        very old Node, install a newer one via your package manager or{' '}
        <a
          href="https://github.com/nvm-sh/nvm"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-primary"
        >
          nvm
        </a>
        .
      </Step>
      <Step num={2}>
        Install Claude Code: <Code>npm install -g @anthropic-ai/claude-code</Code>. Any other AI
        coding agent that can run shell commands and edit files also works.
      </Step>
      <Step num={3}>
        Run your AI agent and log in. You&apos;ll need access to the public npm registry so the{' '}
        <Code>@fias/fias-plugin</Code> scaffold can be pulled.
      </Step>
    </StepList>
  );
}

function StepList({ children }: { children: React.ReactNode }) {
  return <ol className="space-y-2.5 text-sm">{children}</ol>;
}

function Step({ num, children }: { num: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold mt-0.5">
        {num}
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">{children}</code>;
}

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Your browser blocked clipboard access. Select and copy the text manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="relative">
      <pre className="rounded-md border bg-muted/40 px-3 py-3 pr-10 text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">
        {text}
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="absolute top-1.5 right-1.5 h-7 w-7 p-0"
        aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}
